<script lang="ts">
import { defineComponent } from 'vue';
import { InteractiveContentPictureCounts } from '@/models/index';

export default defineComponent({
  name: 'ThirdNotification',
  created () {
    window.scrollTo(0, 0);
  },
  data () {
    return {
      count: 0
    };
  },
  methods: {
    onImgLoad () {
      this.count++;
      if (this.count === InteractiveContentPictureCounts.oldContent) {
        this.$emit('scroll');
        this.$emit('load');
      }
    },
    click (link: string) {
      window.parent.postMessage({
        solutoContent: link
      }, '*');
    },
    gotoSection (id: string) {
      const box = document.getElementById(id);
      if (box) {
        window.parent.postMessage({
          solutoScroll: { position: box.offsetTop, delay: 500 }
        }, '*');
      }
    }
  }
});
</script>

<template>
  <main class="main" id="main">
      <div class="contents__manual is-color-green">
        <section class="section__manual section__manual__mv">
          <div class="inner__manual">
            <!-- ▽ header H1 ▽ -->
            <h1 class="ttl__h1__manual">スマホが医療機関との距離を近づける「オンライン診療」とは？</h1>
            <!-- ▽ MV Image ▽ -->
            <div class="mv__manual"><img @load="onImgLoad" class="has-bgcolor" src="../../assets/recommendation/manual-03_img1.gif" alt=""/></div>
            <!-- ▽ Text ▽-->
            <p class="txt__manual">スマートフォンはWEBを通じてさまざまな世界と簡単につながり、生活のお困りごとを解消してくれるのが便利なポイント！体調が少し悪いかな…というとき、おうち時間でお医者さんのオンライン診療を受けられるアプリサービスもあります。</p>
          </div>
        </section>
        <!-- ▽ Navigation ▽-->
        <div class="nav__manual">
          <ul class="nav__wrap__manual">
            <li class="nav__menu__manual"><a @click="gotoSection('content1')">スマホで手早くお医者さんに頼る</a></li>
            <li class="nav__menu__manual"><a @click="gotoSection('content2')">オンラインならではのメリット</a></li>
            <li class="nav__menu__manual"><a @click="gotoSection('content3')">まずアプリのインストール</a></li>
            <li class="nav__menu__manual"><a @click="gotoSection('content4')">通院の困難を解決する新手法</a></li>
          </ul>
        </div>
        <section class="section__manual section__manual__default section__manual__colored" id="content1">
          <div class="inner__manual">
            <!-- ▽ header H2 ▽-->
            <h2 class="ttl__h2__manual">スマホで手早くお医者さんに頼る</h2>
            <!-- ▽ Text ▽-->
            <p class="txt__manual">「あれ、ちょっと熱っぽいかな」と体調の変化に気がつきながら、移動にも時間がかかるため病院へ行く時間がつくれず、無理をしてしまう。ですが、後々の悪化や周囲への影響があるのではと心配。気持ち的にもストレスになり、余計に体調が悪くなってしまう。こんな負のスパイラルから脱出するためにも、お手元のスマートフォンからお医者さんを頼りましょう。それが、「オンライン診療」です。</p>
            <!-- ▽ Image ▽-->
            <div class="img__manual"><img @load="onImgLoad" src="../../assets/recommendation/manual-03_img1.png" alt=""/></div>
          </div>
        </section>
        <section class="section__manual section__manual__default" id="content2">
          <div class="inner__manual">
            <!-- ▽ header H2 ▽-->
            <h2 class="ttl__h2__manual">オンラインならではのメリット</h2>
            <!-- ▽ Text ▽-->
            <p class="txt__manual">スマートフォンやタブレット、PCなどを利用して、病院の予約や受付から診療後の決済まで実際に医療機関に足を運ばなくても診察・治療を受けられることを「オンライン診療」と呼びます。メリットとしては主に次のことが挙げられます。</p>
            <!-- ▽ List（番号を自動付与） ▽-->
            <ol class="list__manual">
              <li>
                <!-- ▽ List Title ▽-->
                <h4 class="list__ttl__manual"><span class="list__num__manual"><span class="num"></span></span><span class="list__txt__manual">受付＆会計で待つ時間が短くなる</span></h4>
              </li>
              <li>
                <!-- ▽ List Title ▽-->
                <h4 class="list__ttl__manual"><span class="list__num__manual"><span class="num"></span></span><span class="list__txt__manual">他の患者と会わずプライバシーが守られる</span></h4>
              </li>
              <li>
                <!-- ▽ List Title ▽-->
                <h4 class="list__ttl__manual"><span class="list__num__manual"><span class="num"></span></span><span class="list__txt__manual">院内感染および二次感染のリスクの減少</span></h4>
              </li>
            </ol>
            <!-- ▽ Image ▽-->
            <div class="img__manual"><img @load="onImgLoad" src="../../assets/recommendation/manual-03_img2.png" alt=""/></div>
            <!-- ▽ Text ▽-->
            <p class="txt__manual">もちろん、オンラインで行われるのですから、視診や触診などが必要となる診察には向いていません。検査などが必要になる初診には適さないかもしれませんが、継続的な経過をチェックする“再診”や定期的に通い、こちらの身体をよく知ってもらっているかかりつけ医の診察などであれば、便利に利用できるでしょう。</p>
            <p class="note__manual">※患者の疾患や状態等によりオンライン診療では受診できない場合があります。主治医とご相談のうえご利用をおすすめします。<br/>※オンライン診療に関する詳細は<a class="link" @click="click('https://www.mhlw.go.jp/stf/seisakunitsuite/bunya/kenkou_iryou/iryou/rinsyo/index_00010.html')"  target="_blank">「オンライン診療に関するホームページ｜厚生労働省」</a>をご覧ください。</p>
          </div>
        </section>
        <section class="section__manual section__manual__default section__manual__colored" id="content3">
          <div class="inner__manual">
            <!-- ▽ header H2 ▽-->
            <h2 class="ttl__h2__manual">まずアプリのインストール</h2>
            <!-- ▽ Text ▽-->
            <p class="txt__manual">試してみたいと思った場合には、まず専用のアプリをインストールする必要があります。受診を考える医療機関が指定する「オンライン診療アプリ」を確認し、ダウンロードからはじめましょう。</p>
            <!-- ▽ Image ▽-->
            <div class="img__manual"><img @load="onImgLoad" src="../../assets/recommendation/manual-03_img3.png" alt=""/></div>
            <!-- ▽ Text ▽-->
            <p class="txt__manual">例えば、「CLINICS（クリニクス）」というアプリは、日本全国1,000以上の医療機関で導入（2020年4月現在）されていてアプリ自体の利用料は無料。まだ「検討してから」という方にとっては、頼りにする病院の選択肢も多く、使い勝手がいいかもしれません。</p>
          </div>
        </section>
        <section class="section__manual section__manual__default" id="content4">
          <div class="inner__manual">
            <!-- ▽ header H2 ▽-->
            <h2 class="ttl__h2__manual">通院の困難を解決する新手法</h2>
            <!-- ▽ Text ▽-->
            <p class="txt__manual">オンライン診療は症状により適さないこともあり、病気や体調の変化でお困りのすべての皆さんにおすすめできるわけではないのですが、存在すら知らなかった方には通院の困難を解消する手段が増えるわけです。電話やメールだけの利用ではもったいない、スマートフォンの可能性と未来がそこにあるのではないでしょうか。</p>
          </div>
        </section>
        <section class="section__manual section__manual__default">
          <div class="inner__manual">
            <p class="note__manual">※掲載している情報は、2020年7月10日現在のものです。内容・仕様など予告なしに変更されることがありますのであらかじめご了承ください。</p>
          </div>
        </section>
        <!-- ▽ Conversion area ▽-->
        <div class="conversion__manual">
          <div class="conversion__wrap__manual">
            <div class="conversion__app__manual">
              <!-- ▽ Conversion Icon ▽-->
              <div class="conversion__icon__manual">
                <div class="icon"><img @load="onImgLoad" src="../../assets/recommendation/manual-03_img4.png" alt=""/></div>
              </div>
              <!-- ▽ Conversion Title ▽-->
              <p class="conversion__ttl__manual"><span class="ttl">CLINICS (クリニクス)<br/>for auスマートパス</span><span class="txt">価格：無料<br/>※価格は変更される場合がございます</span></p>
            </div>
            <!-- ▽ Conversion Links ▽-->
            <ul class="conversion__links__manual">
              <li class="conversion__link__manual">
                <p class="txt">Google Playは<a class="link" @click="click('https://play.google.com/store/apps/details?id=life.medley.clinics')" target="_blank">こちら</a>から</p>
              </li>
              <li class="conversion__link__manual">
                <p class="txt">AppStoreは<a class="link" @click="click('https://apps.apple.com/jp/app/clinics-%E3%82%AF%E3%83%AA%E3%83%8B%E3%82%AF%E3%82%B9/id1106261604')" target="_blank">こちら</a>から</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </main>
</template>

<style lang="scss" scoped>
@import "@/styles/manual_notice.scss";
</style>
